import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { getAddresses, createAddress, updateAddress, deleteAddress } from '../../services/supabaseService';
import { UserAddress } from '../../types';
import { useNotification } from '../../components/member/NotificationProvider';

export const ManageAddresses: React.FC = () => {
    const { showNotification } = useNotification();
    const [addresses, setAddresses] = useState<UserAddress[]>([]);
    const [loading, setLoading] = useState(true);
    const [isAdding, setIsAdding] = useState(false);
    const [isEditing, setIsEditing] = useState<string | null>(null);
    const [formData, setFormData] = useState({
        label: '',
        street: '',
        number: '',
        neighborhood: '',
        complement: '',
        city: '',
        state: '',
        cep: '',
        reference: '',
        is_default: false
    });
    const [saving, setSaving] = useState(false);
    const [isSearchingCep, setIsSearchingCep] = useState(false);
    const [cepStatus, setCepStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const numberInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        loadAddresses();
    }, []);

    const loadAddresses = async () => {
        setLoading(true);
        try {
            const data = await getAddresses();
            setAddresses(data);
        } catch (error) {
            console.error('Error loading addresses:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCep = async (cleanCep: string) => {
        if (cleanCep.length !== 8) return;

        setIsSearchingCep(true);
        setCepStatus('loading');
        try {
            const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
            if (!response.ok) {
                throw new Error(`HTTP error ${response.status}`);
            }
            const data = await response.json();

            if (data.erro === true || data.erro === 'true') {
                setCepStatus('error');
                showNotification('CEP não encontrado. Por favor, preencha o endereço manualmente.', 'info');
                return;
            }

            setFormData(prev => ({
                ...prev,
                street: data.logradouro || prev.street,
                neighborhood: data.bairro || prev.neighborhood,
                city: data.localidade || prev.city,
                state: data.uf ? data.uf.toUpperCase() : prev.state,
                complement: prev.complement || data.complemento || '',
            }));

            setCepStatus('success');
            showNotification('Endereço autocompletado com dados do ViaCEP!', 'success');

            // Focar automaticamente no campo número para agilizar o preenchimento
            setTimeout(() => {
                numberInputRef.current?.focus();
            }, 150);
        } catch (err) {
            console.error('Erro ao consultar ViaCEP:', err);
            setCepStatus('error');
            showNotification('Não foi possível consultar o CEP no ViaCEP. Preencha manualmente.', 'error');
        } finally {
            setIsSearchingCep(false);
        }
    };

    const handleCepChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value.replace(/\D/g, '').slice(0, 8);
        const formatted = raw.length > 5 ? `${raw.slice(0, 5)}-${raw.slice(5)}` : raw;

        setFormData(prev => ({ ...prev, cep: formatted }));

        if (raw.length === 8) {
            fetchCep(raw);
        } else {
            setCepStatus('idle');
        }
    };

    const handleCepBlur = () => {
        const clean = (formData.cep || '').replace(/\D/g, '');
        if (clean.length === 8 && cepStatus === 'idle') {
            fetchCep(clean);
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...formData,
                cep: formData.cep.replace(/\D/g, '')
            };

            if (isEditing) {
                await updateAddress(isEditing, payload);
                showNotification('Endereço atualizado com sucesso!', 'success');
            } else {
                await createAddress(payload);
                showNotification('Endereço criado com sucesso!', 'success');
            }
            await loadAddresses();
            resetForm();
        } catch (error) {
            console.error('Error saving address:', error);
            showNotification('Erro ao salvar endereço. Verifique os dados.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Tem certeza que deseja excluir este endereço?')) return;
        try {
            await deleteAddress(id);
            showNotification('Endereço excluído com sucesso!', 'success');
            await loadAddresses();
        } catch (error) {
            console.error('Error deleting address:', error);
            showNotification('Erro ao excluir endereço.', 'error');
        }
    };

    const resetForm = () => {
        setFormData({
            label: '',
            street: '',
            number: '',
            neighborhood: '',
            complement: '',
            city: '',
            state: '',
            cep: '',
            reference: '',
            is_default: false
        });
        setIsSearchingCep(false);
        setCepStatus('idle');
        setIsAdding(false);
        setIsEditing(null);
    };

    const startEdit = (address: UserAddress) => {
        const rawCep = (address.cep || '').replace(/\D/g, '');
        const formattedCep = rawCep.length === 8 ? `${rawCep.slice(0, 5)}-${rawCep.slice(5)}` : (address.cep || '');

        setFormData({
            label: address.label || '',
            street: address.street || '',
            number: address.number || '',
            neighborhood: address.neighborhood || '',
            complement: address.complement || '',
            city: address.city || '',
            state: address.state || '',
            cep: formattedCep,
            reference: address.reference || '',
            is_default: address.is_default
        });
        setIsSearchingCep(false);
        setCepStatus('idle');
        setIsEditing(address.id);
        setIsAdding(true);
    };

    return (
        <div className="max-w-4xl 2xl:max-w-5xl mx-auto py-5 px-3 sm:px-4">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 text-white">
                <div className="flex items-center space-x-3">
                    <div className="w-9 h-9 rounded-xl bg-purple-950/60 border border-purple-500/20 text-purple-400 flex items-center justify-center text-sm shrink-0">
                        <i className="fas fa-location-dot"></i>
                    </div>
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold uppercase tracking-tight">Meus Endereços</h1>
                        <p className="text-xs text-slate-400">Gerencie seus endereços para cálculo de frete e entregas</p>
                    </div>
                </div>
                {!isAdding && (
                    <button 
                        onClick={() => setIsAdding(true)}
                        className="bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-lg font-bold text-xs uppercase tracking-wider transition-all flex items-center space-x-1.5 shadow-md shadow-purple-600/20 active:scale-95 shrink-0 self-start sm:self-auto cursor-pointer"
                    >
                        <i className="fas fa-plus text-[11px]"></i>
                        <span>Novo Endereço</span>
                    </button>
                )}
            </div>

            <AnimatePresence>
                {isAdding && (
                    <motion.div 
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 15 }}
                        className="bg-slate-900 border border-slate-800 p-4 sm:p-5 rounded-xl mb-6 shadow-xl shadow-black/40"
                    >
                        <form onSubmit={handleSave} className="space-y-4">
                            <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                                <h2 className="text-sm font-bold text-white flex items-center space-x-2">
                                    <i className="fas fa-location-dot text-purple-400 text-xs"></i>
                                    <span>{isEditing ? 'Editar Endereço' : 'Cadastrar Novo Endereço'}</span>
                                </h2>
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    className="text-slate-400 hover:text-white text-xs font-semibold uppercase tracking-wider flex items-center space-x-1 cursor-pointer"
                                >
                                    <i className="fas fa-times text-xs"></i>
                                    <span>Fechar</span>
                                </button>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                {/* 1. CEP NO INÍCIO DO FORMULÁRIO (DESTAQUE PARA INDUZIR PREENCHIMENTO PRIMEIRO) */}
                                <div className="md:col-span-4 bg-gradient-to-br from-purple-950/30 via-slate-900/90 to-slate-900 border border-purple-500/25 rounded-xl p-3 sm:p-3.5">
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-1.5">
                                        <label className="text-[11px] font-bold uppercase tracking-wider text-purple-300 flex items-center space-x-1.5">
                                            <i className="fas fa-map-pin text-purple-400 text-xs"></i>
                                            <span>CEP (Preencha primeiro para autocompletar)</span>
                                            <span className="text-red-400">*</span>
                                        </label>
                                        {isSearchingCep && (
                                            <span className="text-[11px] text-purple-300 flex items-center space-x-1.5 font-semibold animate-pulse">
                                                <i className="fas fa-circle-notch fa-spin text-purple-400 text-[10px]"></i>
                                                <span>Consultando ViaCEP...</span>
                                            </span>
                                        )}
                                        {!isSearchingCep && cepStatus === 'success' && (
                                            <span className="text-[11px] text-emerald-400 flex items-center space-x-1 font-bold">
                                                <i className="fas fa-check-circle text-[10px]"></i>
                                                <span>Endereço autocompletado!</span>
                                            </span>
                                        )}
                                        {!isSearchingCep && cepStatus === 'error' && (
                                            <span className="text-[11px] text-amber-400 flex items-center space-x-1 font-semibold">
                                                <i className="fas fa-exclamation-triangle text-[10px]"></i>
                                                <span>CEP não localizado (preencha abaixo)</span>
                                            </span>
                                        )}
                                    </div>
                                    
                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <div className="relative flex-1">
                                            <input 
                                                required
                                                type="text" 
                                                inputMode="numeric"
                                                maxLength={9}
                                                value={formData.cep || ''}
                                                onChange={handleCepChange}
                                                onBlur={handleCepBlur}
                                                placeholder="00000-000"
                                                className="w-full bg-slate-950/80 border border-purple-500/35 rounded-lg px-3 py-2 text-white font-mono text-xs sm:text-sm tracking-wider placeholder:text-slate-600 focus:outline-none focus:ring-1.5 focus:ring-purple-500 focus:border-purple-500 transition-all"
                                            />
                                            {isSearchingCep && (
                                                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-purple-400 text-xs">
                                                    <i className="fas fa-spinner fa-spin"></i>
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const clean = (formData.cep || '').replace(/\D/g, '');
                                                if (clean.length === 8) {
                                                    fetchCep(clean);
                                                } else {
                                                    showNotification('Digite um CEP válido com 8 números.', 'info');
                                                }
                                            }}
                                            disabled={isSearchingCep}
                                            className="px-3.5 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white rounded-lg font-bold text-xs uppercase tracking-wider transition-all flex items-center justify-center space-x-1.5 shadow-sm shadow-purple-600/30 active:scale-95 shrink-0 cursor-pointer"
                                        >
                                            <i className="fas fa-magnifying-glass text-[11px]"></i>
                                            <span>Buscar CEP</span>
                                        </button>
                                    </div>
                                    <p className="text-[10px] text-slate-400 mt-1.5 flex items-center space-x-1">
                                        <i className="fas fa-bolt text-amber-400 text-[9px]"></i>
                                        <span>Rua, bairro, cidade e estado são preenchidos automaticamente.</span>
                                    </p>
                                </div>

                                {/* Identificação (Apelido do Endereço) */}
                                <div className="md:col-span-4">
                                    <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                                        Identificação do Endereço (Ex: Casa, Trabalho, Casa de Praia)
                                    </label>
                                    <input 
                                        type="text" 
                                        value={formData.label || ''}
                                        onChange={e => setFormData({ ...formData, label: e.target.value })}
                                        placeholder="Ex: Minha Casa"
                                        className="w-full bg-slate-800/80 border border-slate-700/60 rounded-lg px-3 py-2 text-xs sm:text-sm text-white placeholder:text-slate-500 focus:ring-1.5 focus:ring-purple-600 focus:outline-none transition-all"
                                    />
                                </div>

                                {/* Rua / Logradouro (3 cols) + Número (1 col) */}
                                <div className="md:col-span-3">
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Rua / Logradouro</label>
                                        {cepStatus === 'success' && (
                                            <span className="text-[9px] text-purple-400 font-bold uppercase tracking-wider">Preenchido via CEP</span>
                                        )}
                                    </div>
                                    <input 
                                        required
                                        type="text" 
                                        value={formData.street || ''}
                                        onChange={e => setFormData({ ...formData, street: e.target.value })}
                                        placeholder="Ex: Rua das Flores"
                                        className="w-full bg-slate-800/80 border border-slate-700/60 rounded-lg px-3 py-2 text-xs sm:text-sm text-white placeholder:text-slate-500 focus:ring-1.5 focus:ring-purple-600 focus:outline-none transition-all"
                                    />
                                </div>
                                <div className="md:col-span-1">
                                    <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                                        Número <span className="text-purple-400 font-bold">*</span>
                                    </label>
                                    <input 
                                        ref={numberInputRef}
                                        required
                                        type="text" 
                                        value={formData.number || ''}
                                        onChange={e => setFormData({ ...formData, number: e.target.value })}
                                        placeholder="Ex: 123"
                                        className="w-full bg-slate-800/80 border border-slate-700/60 rounded-lg px-3 py-2 text-xs sm:text-sm text-white placeholder:text-slate-500 focus:ring-1.5 focus:ring-purple-600 focus:outline-none transition-all font-semibold"
                                    />
                                </div>
                                
                                {/* Bairro e Complemento */}
                                <div className="md:col-span-2">
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Bairro</label>
                                        {cepStatus === 'success' && (
                                            <span className="text-[9px] text-purple-400 font-bold uppercase tracking-wider">Preenchido</span>
                                        )}
                                    </div>
                                    <input 
                                        required
                                        type="text" 
                                        value={formData.neighborhood || ''}
                                        onChange={e => setFormData({ ...formData, neighborhood: e.target.value })}
                                        placeholder="Ex: Centro"
                                        className="w-full bg-slate-800/80 border border-slate-700/60 rounded-lg px-3 py-2 text-xs sm:text-sm text-white placeholder:text-slate-500 focus:ring-1.5 focus:ring-purple-600 focus:outline-none transition-all"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Complemento (Opcional)</label>
                                    <input 
                                        type="text" 
                                        value={formData.complement || ''}
                                        onChange={e => setFormData({ ...formData, complement: e.target.value })}
                                        placeholder="Ex: Apto 42, Bloco B"
                                        className="w-full bg-slate-800/80 border border-slate-700/60 rounded-lg px-3 py-2 text-xs sm:text-sm text-white placeholder:text-slate-500 focus:ring-1.5 focus:ring-purple-600 focus:outline-none transition-all"
                                    />
                                </div>

                                {/* Cidade (3 cols) e Estado UF (1 col) */}
                                <div className="md:col-span-3">
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Cidade</label>
                                        {cepStatus === 'success' && (
                                            <span className="text-[9px] text-purple-400 font-bold uppercase tracking-wider">Preenchido</span>
                                        )}
                                    </div>
                                    <input 
                                        required
                                        type="text" 
                                        value={formData.city || ''}
                                        onChange={e => setFormData({ ...formData, city: e.target.value })}
                                        placeholder="Ex: São Paulo"
                                        className="w-full bg-slate-800/80 border border-slate-700/60 rounded-lg px-3 py-2 text-xs sm:text-sm text-white placeholder:text-slate-500 focus:ring-1.5 focus:ring-purple-600 focus:outline-none transition-all"
                                    />
                                </div>
                                <div className="md:col-span-1">
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">UF</label>
                                        {cepStatus === 'success' && (
                                            <span className="text-[9px] text-purple-400 font-bold uppercase tracking-wider">Preenchido</span>
                                        )}
                                    </div>
                                    <input 
                                        required
                                        type="text" 
                                        maxLength={2}
                                        value={formData.state || ''}
                                        onChange={e => setFormData({ ...formData, state: e.target.value.toUpperCase() })}
                                        placeholder="SP"
                                        className="w-full bg-slate-800/80 border border-slate-700/60 rounded-lg px-3 py-2 text-xs sm:text-sm text-white placeholder:text-slate-500 uppercase font-mono text-center focus:ring-1.5 focus:ring-purple-600 focus:outline-none transition-all"
                                    />
                                </div>
                                
                                {/* Ponto de Referência */}
                                <div className="md:col-span-4">
                                    <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Ponto de Referência (Opcional)</label>
                                    <input 
                                        type="text" 
                                        value={formData.reference || ''}
                                        onChange={e => setFormData({ ...formData, reference: e.target.value })}
                                        placeholder="Ex: Próximo à padaria central, portão verde"
                                        className="w-full bg-slate-800/80 border border-slate-700/60 rounded-lg px-3 py-2 text-xs sm:text-sm text-white placeholder:text-slate-500 focus:ring-1.5 focus:ring-purple-600 focus:outline-none transition-all"
                                    />
                                </div>
                            </div>

                            {/* Padrão checkbox */}
                            <div className="flex items-center space-x-2.5 pt-1">
                                <input 
                                    type="checkbox" 
                                    id="is_default"
                                    checked={formData.is_default}
                                    onChange={e => setFormData({ ...formData, is_default: e.target.checked })}
                                    className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-purple-600 focus:ring-purple-600 cursor-pointer"
                                />
                                <label htmlFor="is_default" className="text-xs text-slate-300 font-medium cursor-pointer select-none">
                                    Definir como endereço padrão para pedidos
                                </label>
                            </div>

                            <div className="flex items-center justify-end space-x-2.5 pt-3 border-t border-slate-800/80">
                                <button 
                                    type="button"
                                    onClick={resetForm}
                                    className="px-4 py-2 rounded-lg font-semibold text-xs text-slate-400 hover:text-white transition-colors cursor-pointer"
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="submit"
                                    disabled={saving}
                                    className="bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white px-5 py-2 rounded-lg font-bold text-xs uppercase tracking-wider transition-all shadow-md shadow-purple-600/30 flex items-center space-x-1.5 cursor-pointer"
                                >
                                    {saving && <i className="fas fa-circle-notch fa-spin text-xs"></i>}
                                    <span>{saving ? 'Salvando...' : 'Salvar Endereço'}</span>
                                </button>
                            </div>
                        </form>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* LISTAGEM DE ENDEREÇOS */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                {loading ? (
                    Array(2).fill(0).map((_, i) => (
                        <div key={i} className="h-36 bg-slate-900/50 border border-slate-800/50 rounded-xl animate-pulse" />
                    ))
                ) : addresses.length === 0 ? (
                    <div className="md:col-span-2 text-center py-12 px-4 bg-slate-900/40 border border-dashed border-slate-800 rounded-xl">
                        <i className="fas fa-map-location-dot text-3xl text-slate-700 mb-2.5 block"></i>
                        <p className="text-slate-400 text-xs font-medium">Nenhum endereço cadastrado ainda.</p>
                        <button 
                            onClick={() => setIsAdding(true)}
                            className="mt-3 text-purple-400 hover:text-purple-300 text-xs font-bold flex items-center justify-center space-x-1.5 mx-auto cursor-pointer"
                        >
                            <i className="fas fa-plus text-[10px]"></i>
                            <span>Clique aqui para adicionar seu primeiro endereço</span>
                        </button>
                    </div>
                ) : (
                    addresses.map(address => {
                        const rawCep = (address.cep || '').replace(/\D/g, '');
                        const displayCep = rawCep.length === 8 ? `${rawCep.slice(0, 5)}-${rawCep.slice(5)}` : (address.cep || '-');

                        return (
                            <motion.div 
                                layout
                                key={address.id}
                                className={`p-4 rounded-xl border transition-all ${address.is_default ? 'bg-purple-600/10 border-purple-500/50 shadow-sm shadow-purple-600/10' : 'bg-slate-900 border-slate-800 hover:border-slate-700'}`}
                            >
                                <div className="flex justify-between items-start mb-2.5">
                                    <div className="flex items-center space-x-2.5">
                                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs shrink-0 ${address.is_default ? 'bg-purple-600 text-white' : 'bg-slate-800 text-slate-400'}`}>
                                            <i className="fas fa-map-marker-alt text-[11px]"></i>
                                        </div>
                                        <div>
                                            <div className="flex items-center space-x-2">
                                                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-200">
                                                    {address.label || 'Endereço'}
                                                </h3>
                                                {address.is_default && (
                                                    <span className="text-[9px] font-black uppercase tracking-widest text-purple-400 bg-purple-400/10 px-1.5 py-0.5 rounded">
                                                        PADRÃO
                                                    </span>
                                                )}
                                            </div>
                                            <span className="text-[11px] font-mono text-purple-400/90 font-medium">
                                                CEP: {displayCep}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-0.5">
                                        <button 
                                            onClick={() => startEdit(address)}
                                            className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-white transition-colors rounded-lg hover:bg-slate-800 cursor-pointer"
                                            title="Editar endereço"
                                        >
                                            <i className="fas fa-pen text-xs"></i>
                                        </button>
                                        <button 
                                            onClick={() => handleDelete(address.id)}
                                            className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-red-400 transition-colors rounded-lg hover:bg-slate-800 cursor-pointer"
                                            title="Excluir endereço"
                                        >
                                            <i className="fas fa-trash-can text-xs"></i>
                                        </button>
                                    </div>
                                </div>
                                
                                <div className="space-y-1 text-xs">
                                    <p className="text-white font-semibold leading-snug">
                                        {address.street}, {address.number}
                                    </p>
                                    {address.complement && (
                                        <p className="text-slate-300 text-[11px]">
                                            {address.complement}
                                        </p>
                                    )}
                                    <p className="text-slate-400 text-[11px]">
                                        {address.neighborhood} • {address.city}/{address.state}
                                    </p>
                                    {address.reference && (
                                        <div className="mt-2 pt-2 border-t border-slate-800/80">
                                            <p className="text-[9px] text-slate-500 uppercase font-bold tracking-wider">Ponto de Referência</p>
                                            <p className="text-slate-400 text-[11px] mt-0.5">{address.reference}</p>
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        );
                    })
                )}
            </div>
        </div>
    );
};
